import { useEffect, useRef } from 'react';

import { Alert } from '../components/alert';

export interface AuthErrorSummaryProps {
  /** Null when there is nothing to report. */
  message: string | null;
}

/**
 * One error region per form, announced and focused when it appears (rules §48).
 *
 * The same component the patient app uses, lifted here so the admin and super admin
 * auth screens make the same promise: a failure is *told* to a screen-reader or
 * keyboard user rather than left for them to find. `tabIndex={-1}` makes it focusable
 * without adding it to the tab order afterwards.
 */
export function AuthErrorSummary({ message }: AuthErrorSummaryProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (message === null) return;
    ref.current?.focus();
  }, [message]);

  if (message === null) return null;

  return (
    <div
      ref={ref}
      tabIndex={-1}
      className="outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
    >
      <Alert tone="error">{message}</Alert>
    </div>
  );
}

/**
 * The fallback sentence for a failure the caller has not taught the component about.
 *
 * Every auth component takes a `describeError` prop, because the API writes its own
 * messages to be shown (API.md §1.6) and only the app has the `ApiError` type to read
 * them from. This is what they fall back to.
 */
export function describeUnknownError(error: unknown): string {
  if (typeof error === 'string' && error.trim() !== '') return error;
  if (error instanceof Error && error.message !== '') return error.message;
  return 'Something went wrong. Please try again.';
}
